import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PasswordStrengthChecklist, { PASSWORD_REGEX } from '@/components/PasswordStrengthChecklist';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Building2, Loader2, CheckCircle2, AlertCircle, Users, Mail, Lock, User, Briefcase, Globe, Check, ChevronsUpDown, CreditCard, Sparkles, ArrowLeft, Shield, Clock, Zap, Calendar, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import api from '@/services/api';
import departmentService from '@/services/departmentService';
import { Badge } from '@/components/ui/badge';
import { LocationAutocomplete, LocationResult } from '@/components/ui/location-autocomplete';

// Searchable Combobox component for dropdowns with more than 5 items
interface SearchableSelectProps {
  options: string[];
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  emptyMessage?: string;
}

const SearchableSelect = ({
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder = "Search...",
  disabled = false,
  emptyMessage = "No results found.",
}: SearchableSelectProps) => {
  const [open, setOpen] = useState(false);

  // If 5 or fewer options, use simple Select
  if (options.length <= 5) {
    return (
      <Select onValueChange={onChange} value={value} disabled={disabled}>
        <SelectTrigger className="mt-1">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  // For more than 5 options, use searchable Combobox
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between mt-1 font-normal",
            !value && "text-muted-foreground"
          )}
          disabled={disabled}
        >
          {value || placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option}
                  value={option}
                  onSelect={() => {
                    onChange(option);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === option ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {option}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

// Industry options
const INDUSTRIES = [
  'Agriculture & Farming',
  'Automotive',
  'Banking & Financial Services',
  'Chemical & Petrochemical',
  'Construction & Real Estate',
  'Consumer Goods & Retail',
  'Education',
  'Energy & Utilities',
  'Food & Beverage',
  'Healthcare & Pharmaceuticals',
  'Hospitality & Tourism',
  'Information Technology',
  'Insurance',
  'Manufacturing',
  'Media & Entertainment',
  'Mining & Metals',
  'Oil & Gas',
  'Professional Services',
  'Telecommunications',
  'Textiles & Apparel',
  'Transportation & Logistics',
  'Other',
];

// Country and City data
const COUNTRIES_WITH_CITIES: Record<string, string[]> = {
  'India': [
    'Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Chennai', 'Kolkata', 'Pune', 'Ahmedabad',
    'Jaipur', 'Lucknow', 'Kanpur', 'Nagpur', 'Indore', 'Thane', 'Bhopal', 'Visakhapatnam',
    'Patna', 'Vadodara', 'Ghaziabad', 'Ludhiana', 'Agra', 'Nashik', 'Faridabad', 'Meerut',
    'Rajkot', 'Varanasi', 'Srinagar', 'Aurangabad', 'Dhanbad', 'Amritsar', 'Navi Mumbai',
    'Allahabad', 'Ranchi', 'Howrah', 'Coimbatore', 'Jabalpur', 'Gwalior', 'Vijayawada',
    'Jodhpur', 'Madurai', 'Raipur', 'Kota', 'Chandigarh', 'Guwahati', 'Solapur', 'Hubli',
    'Mysore', 'Tiruchirappalli', 'Bareilly', 'Aligarh', 'Tiruppur', 'Moradabad', 'Jalandhar',
    'Bhubaneswar', 'Salem', 'Warangal', 'Guntur', 'Bhiwandi', 'Saharanpur', 'Gorakhpur',
    'Bikaner', 'Amravati', 'Noida', 'Jamshedpur', 'Bhilai', 'Cuttack', 'Firozabad', 'Kochi',
    'Nellore', 'Bhavnagar', 'Dehradun', 'Durgapur', 'Asansol', 'Rourkela', 'Nanded', 'Kolhapur',
    'Ajmer', 'Akola', 'Gulbarga', 'Jamnagar', 'Ujjain', 'Loni', 'Siliguri', 'Jhansi',
    'Ulhasnagar', 'Jammu', 'Sangli-Miraj & Kupwad', 'Mangalore', 'Erode', 'Belgaum', 'Ambattur',
    'Tirunelveli', 'Malegaon', 'Gaya', 'Jalgaon', 'Udaipur', 'Maheshtala', 'Other'
  ],
  'United States': [
    'New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix', 'Philadelphia', 'San Antonio',
    'San Diego', 'Dallas', 'San Jose', 'Austin', 'Jacksonville', 'Fort Worth', 'Columbus',
    'Charlotte', 'San Francisco', 'Indianapolis', 'Seattle', 'Denver', 'Washington', 'Boston',
    'El Paso', 'Nashville', 'Detroit', 'Oklahoma City', 'Portland', 'Las Vegas', 'Memphis',
    'Louisville', 'Baltimore', 'Milwaukee', 'Albuquerque', 'Tucson', 'Fresno', 'Sacramento',
    'Kansas City', 'Mesa', 'Atlanta', 'Omaha', 'Colorado Springs', 'Raleigh', 'Miami',
    'Long Beach', 'Virginia Beach', 'Oakland', 'Minneapolis', 'Tampa', 'Tulsa', 'Arlington',
    'New Orleans', 'Other'
  ],
  'United Kingdom': [
    'London', 'Birmingham', 'Manchester', 'Leeds', 'Glasgow', 'Liverpool', 'Newcastle',
    'Sheffield', 'Bristol', 'Edinburgh', 'Leicester', 'Coventry', 'Bradford', 'Cardiff',
    'Belfast', 'Nottingham', 'Kingston upon Hull', 'Stoke-on-Trent', 'Wolverhampton',
    'Plymouth', 'Southampton', 'Reading', 'Derby', 'Dudley', 'Aberdeen', 'Northampton',
    'Portsmouth', 'Luton', 'Preston', 'Milton Keynes', 'Norwich', 'Swindon', 'Other'
  ],
  'Germany': [
    'Berlin', 'Hamburg', 'Munich', 'Cologne', 'Frankfurt', 'Stuttgart', 'Düsseldorf',
    'Leipzig', 'Dortmund', 'Essen', 'Bremen', 'Dresden', 'Hanover', 'Nuremberg', 'Duisburg',
    'Bochum', 'Wuppertal', 'Bielefeld', 'Bonn', 'Münster', 'Karlsruhe', 'Mannheim',
    'Augsburg', 'Wiesbaden', 'Mönchengladbach', 'Gelsenkirchen', 'Braunschweig', 'Aachen',
    'Kiel', 'Chemnitz', 'Halle', 'Magdeburg', 'Other'
  ],
  'France': [
    'Paris', 'Marseille', 'Lyon', 'Toulouse', 'Nice', 'Nantes', 'Strasbourg', 'Montpellier',
    'Bordeaux', 'Lille', 'Rennes', 'Reims', 'Le Havre', 'Saint-Étienne', 'Toulon', 'Grenoble',
    'Dijon', 'Angers', 'Nîmes', 'Villeurbanne', 'Saint-Denis', 'Le Mans', 'Aix-en-Provence',
    'Clermont-Ferrand', 'Brest', 'Tours', 'Limoges', 'Amiens', 'Perpignan', 'Other'
  ],
  'Canada': [
    'Toronto', 'Montreal', 'Vancouver', 'Calgary', 'Edmonton', 'Ottawa', 'Winnipeg',
    'Quebec City', 'Hamilton', 'Kitchener', 'London', 'Victoria', 'Halifax', 'Oshawa',
    'Windsor', 'Saskatoon', 'Regina', 'Barrie', 'St. John\'s', 'Kelowna', 'Other'
  ],
  'Australia': [
    'Sydney', 'Melbourne', 'Brisbane', 'Perth', 'Adelaide', 'Gold Coast', 'Canberra',
    'Newcastle', 'Wollongong', 'Geelong', 'Hobart', 'Townsville', 'Cairns', 'Darwin',
    'Toowoomba', 'Ballarat', 'Bendigo', 'Launceston', 'Mackay', 'Rockhampton', 'Other'
  ],
  'Singapore': ['Singapore'],
  'United Arab Emirates': [
    'Dubai', 'Abu Dhabi', 'Sharjah', 'Al Ain', 'Ajman', 'Ras Al Khaimah', 'Fujairah',
    'Umm Al Quwain', 'Other'
  ],
  'Japan': [
    'Tokyo', 'Yokohama', 'Osaka', 'Nagoya', 'Sapporo', 'Fukuoka', 'Kobe', 'Kawasaki',
    'Kyoto', 'Saitama', 'Hiroshima', 'Sendai', 'Kitakyushu', 'Chiba', 'Sakai', 'Niigata',
    'Hamamatsu', 'Shizuoka', 'Sagamihara', 'Okayama', 'Other'
  ],
  'China': [
    'Shanghai', 'Beijing', 'Guangzhou', 'Shenzhen', 'Chengdu', 'Hangzhou', 'Wuhan',
    'Xi\'an', 'Suzhou', 'Nanjing', 'Tianjin', 'Chongqing', 'Qingdao', 'Dalian', 'Ningbo',
    'Zhengzhou', 'Changsha', 'Jinan', 'Harbin', 'Shenyang', 'Other'
  ],
  'Other': ['Other'],
};

const COUNTRIES = Object.keys(COUNTRIES_WITH_CITIES);

// Financial Year Start Month options
const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

// Form validation schema
const companySignupSchema = z.object({
  // Company Information
  companyName: z.string().min(2, 'Company name must be at least 2 characters'),
  industry: z.string().min(1, 'Industry is required'),
  website: z.string().url('Invalid website URL').optional().or(z.literal('')),
  fyStartMonth: z.number().min(1).max(12),

  // Headquarters Address Information
  unitCode: z.string().min(1, 'Unit/Building code is required'),
  addressLine1: z.string().min(1, 'Address Line 1 is required'),
  addressLine2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State/Province is required'),
  country: z.string().min(1, 'Country is required'),
  postalCode: z.string().min(1, 'Postal Code is required'),

  // Admin User Information
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().optional(),
  email: z.string().email('Invalid email address'),
  designation: z.string().min(2, 'Designation is required'),
  department: z.string().min(2, 'Department is required'),
  password: z.string().regex(PASSWORD_REGEX, 'Password must be at least 8 characters and include uppercase, lowercase, a number, and a special character'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type CompanySignupForm = z.infer<typeof companySignupSchema>;

// Plan features based on plan name
const PLAN_FEATURES: Record<string, string[]> = {
  'Starter': [
    'Basic ESG data tracking',
    'Up to 5 users',
    'Standard reports',
    'Email support',
    '1 year data retention',
  ],
  'Professional': [
    'Advanced ESG analytics',
    'Up to 25 users',
    'Custom reports & dashboards',
    'Priority email support',
    '3 years data retention',
    'API access',
    'Scope 1 & 2 emissions tracking',
  ],
  'Enterprise': [
    'Full ESG management suite',
    'Unlimited users',
    'Custom integrations',
    'Dedicated account manager',
    'Unlimited data retention',
    'Full API access',
    'Scope 1, 2 & 3 emissions tracking',
    'Supply chain management',
    'Regulatory compliance tools',
    'White-label options',
  ],
  'default': [
    'ESG data management',
    'Reporting & analytics',
    'User management',
    'Data security',
    'Email support',
  ],
};

// Indian digit grouping (e.g. 500000 -> 5,00,000), no decimal places.
const formatINR = (amount: number): string =>
  Math.round(amount).toLocaleString('en-IN');

const AcceptInvitation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [validatingToken, setValidatingToken] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);
  const [tokenError, setTokenError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [invitationData, setInvitationData] = useState<any>(null);
  const [currentStep, setCurrentStep] = useState<'form' | 'payment'>('form');
  const [formData, setFormData] = useState<CompanySignupForm | null>(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [departmentOptions, setDepartmentOptions] = useState<string[]>([]);

  const token = searchParams.get('token');

  // Department catalog comes from super-admin (same list used in Profile → Department)
  useEffect(() => {
    departmentService
      .getAllDepartmentsPublic()
      .then((departments) => setDepartmentOptions(departments.map((d) => d.name)))
      .catch(() => setDepartmentOptions([]));
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    control,
    watch,
  } = useForm<CompanySignupForm>({
    resolver: zodResolver(companySignupSchema),
    defaultValues: {
      fyStartMonth: 4, // Default to April (common financial year start)
    },
  });

  // Validate token on mount and prefill form with data from super admin
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenValid(false);
        setTokenError('No invitation token provided');
        return;
      }

      setValidatingToken(true);
      try {
        const response = await api.validateInvitation(token);

        if (response.isSuccess && response.data?.valid) {
          setTokenValid(true);
          setInvitationData(response.data);

          // Prefill form with data from super admin
          if (response.data.email) {
            setValue('email', response.data.email);
          }
          if (response.data.firstName) {
            setValue('firstName', response.data.firstName);
          }
          if (response.data.lastName) {
            setValue('lastName', response.data.lastName);
          }
          if (response.data.companyName) {
            setValue('companyName', response.data.companyName);
          }
          if (response.data.industry) {
            setValue('industry', response.data.industry);
          }
          if (response.data.country) {
            setValue('country', response.data.country);
          }
          if (response.data.city) {
            setValue('city', response.data.city);
          }
          if (response.data.website) {
            setValue('website', response.data.website);
          }
        } else {
          setTokenValid(false);
          setTokenError(response.message || 'Invalid invitation token');
        }
      } catch (error: any) {
        setTokenValid(false);
        setTokenError(error.message || 'Failed to validate invitation token');
      } finally {
        setValidatingToken(false);
      }
    };

    validateToken();
  }, [token, setValue]);

  const onSubmit = async (data: CompanySignupForm) => {
    // Payment is already collected upstream (public Razorpay flow or Payment Link),
    // so skip the redundant payment step and activate directly.
    setFormData(data);
    await handlePaymentConfirmation(data);
  };

  const handlePaymentConfirmation = async (dataArg?: CompanySignupForm) => {
    const data = dataArg ?? formData;
    if (!data || !token) return;

    try {
      setProcessingPayment(true);

      const response = await api.confirmPaymentAndSignup({
        token: token,
        company: {
          name: data.companyName,
          industry: data.industry,
          country: data.country,
          city: data.city,
          website: data.website || undefined,
        },
        admin: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          jobTitle: data.designation, // Send as jobTitle to backend
          department: data.department,
          password: data.password,
        },
        location: {
          unitCode: data.unitCode,
          type: 'head', // Set type as 'head' for headquarters location
          address: {
            addressLine1: data.addressLine1,
            addressLine2: data.addressLine2 || undefined,
            city: data.city,
            state: data.state,
            country: data.country,
            postalCode: data.postalCode,
          },
        },
        fyStartMonth: data.fyStartMonth,
      });

      if (response.isSuccess) {
        toast({
          title: 'Success!',
          description: 'Payment confirmed and your company has been registered successfully. Redirecting to dashboard...',
        });

        // Wait a moment then redirect to dashboard
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        toast({
          title: 'Registration Failed',
          description: response.message || 'Failed to complete registration',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'An error occurred during payment confirmation',
        variant: 'destructive',
      });
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleBackToForm = () => {
    setCurrentStep('form');
  };

  // Loading state
  if (validatingToken) {
    return (
      <div className="h-screen overflow-y-auto flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-green-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Loader2 className="w-12 h-12 animate-spin text-blue-500 mb-4" />
              <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-2">
                Validating Invitation
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Please wait while we verify your invitation token...
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid) {
    return (
      <div className="h-screen overflow-y-auto flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-green-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <CardTitle className="text-2xl">Invalid Invitation</CardTitle>
            <CardDescription>
              {tokenError || 'The invitation link is invalid or has expired'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => navigate('/login')}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Payment step
  if (currentStep === 'payment' && formData && invitationData) {
    const planName = invitationData.planName || 'Standard';
    const planDescription = invitationData.planDescription || '';
    const planFeatures = invitationData.planFeatures || PLAN_FEATURES[planName] || PLAN_FEATURES['default'];
    const planFrameworks = invitationData.planFrameworks || [];
    const planMenus = invitationData.planMenus || [];
    const price = invitationData.price || '0';
    const discountPercentage = parseFloat(invitationData.discount || '0');
    const billingCycle = invitationData.billingCycle || 'monthly';
    const originalPrice = parseFloat(price);
    // Calculate the actual discount amount in currency
    const discountAmount = originalPrice * (discountPercentage / 100);
    const finalPrice = invitationData.finalPrice !== undefined ? invitationData.finalPrice : (originalPrice - discountAmount);

    return (
      <div className="h-screen overflow-y-auto bg-gradient-to-br from-blue-50 via-white to-green-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <img src="/logo.png" alt="DataCaffe Logo" className="w-16 h-16 object-contain" />
              <div className="flex flex-col">
                <span className="text-2xl font-bold tracking-tight">
                  <span style={{ color: '#f97316' }}>ESG</span>
                  <span className="text-blue-600 dark:text-blue-400">Caffe</span>
                </span>
                <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                  Green Intelligence
                </span>
              </div>
            </div>
            <h1 className="text-3xl font-bold text-slate-800 dark:text-white mb-2">
              Complete Your Subscription
            </h1>
            <p className="text-slate-500 dark:text-slate-400">
              Review your plan and confirm payment to activate your account
            </p>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex items-center">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-500 text-white">
                <Check className="w-4 h-4" />
              </div>
              <span className="ml-2 text-sm font-medium text-green-600 dark:text-green-400">Company Info</span>
            </div>
            <div className="w-12 h-0.5 bg-green-500 mx-2" />
            <div className="flex items-center">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-500 text-white">
                <CreditCard className="w-4 h-4" />
              </div>
              <span className="ml-2 text-sm font-medium text-blue-600 dark:text-blue-400">Payment</span>
            </div>
          </div>

          {/* Plan Card */}
          <Card className="mb-6 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <Badge variant="secondary" className="mb-2 bg-white/20 text-white border-0">
                    <Sparkles className="w-3 h-3 mr-1" />
                    {planName} Plan
                  </Badge>
                  <h2 className="text-2xl font-bold text-white">{formData.companyName}</h2>
                  <p className="text-blue-100 text-sm">{formData.email}</p>
                </div>
                <div className="text-right">
                  {discountPercentage > 0 && (
                    <p className="text-blue-200 line-through text-sm">
                      ₹{formatINR(originalPrice)}/{billingCycle === 'monthly' ? 'mo' : 'yr'}
                    </p>
                  )}
                  <p className="text-3xl font-bold text-white">
                    ₹{formatINR(finalPrice)}
                  </p>
                  <p className="text-blue-100 text-sm">/{billingCycle === 'monthly' ? 'month' : 'year'}</p>
                </div>
              </div>
            </div>

            <CardContent className="pt-6">
              {/* Discount Banner */}
              {discountPercentage > 0 && (
                <Alert className="mb-6 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
                  <Sparkles className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800 dark:text-green-400">
                    You're saving <strong>₹{formatINR(discountAmount)}</strong> ({discountPercentage}% off) with your special discount!
                  </AlertDescription>
                </Alert>
              )}

              {/* Plan Description */}
              {planDescription && (
                <div className="mb-6">
                  <p className="text-slate-600 dark:text-slate-300">{planDescription}</p>
                </div>
              )}

              {/* Supported Frameworks */}
              {planFrameworks.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-blue-500" />
                    Supported Frameworks
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {planFrameworks.map((framework: any, index: number) => (
                      <Badge key={index} variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                        {framework.code ? `${framework.code} - ${framework.name}` : framework.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Plan Features */}
              {planFeatures.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-blue-500" />
                    What's Included
                  </h3>
                  <ul className="space-y-3">
                    {planFeatures.map((feature: any, index: number) => {
                      // Handle both string and { text, included } / { name, enabled } object shapes
                      const label = typeof feature === 'string' ? feature : feature?.text || feature?.name || '';
                      const enabled = typeof feature === 'string' ? true : (feature?.included ?? feature?.enabled ?? true);
                      if (!label) return null;
                      return (
                        <li key={index} className="flex items-start gap-3">
                          <CheckCircle2 className={`w-5 h-5 flex-shrink-0 mt-0.5 ${enabled ? 'text-green-500' : 'text-slate-400'}`} />
                          <span className={`text-slate-600 dark:text-slate-300 ${!enabled ? 'line-through opacity-60' : ''}`}>{label}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* Available Menu Access */}
              {planMenus.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
                    <Globe className="w-5 h-5 text-blue-500" />
                    Available Modules ({planMenus.length})
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {planMenus.map((menu: any, index: number) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span className="text-sm text-slate-600 dark:text-slate-300 truncate">{menu.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Separator className="my-6" />

              {/* Payment Summary */}
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Plan Price</span>
                  <span className="text-slate-700 dark:text-slate-300">₹{formatINR(originalPrice)}</span>
                </div>
                {discountPercentage > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-green-600">Discount ({discountPercentage}%)</span>
                    <span className="text-green-600">-₹{formatINR(discountAmount)}</span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between font-semibold">
                  <span className="text-slate-800 dark:text-white">Total Due</span>
                  <span className="text-slate-800 dark:text-white text-lg">₹{formatINR(finalPrice)}</span>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="flex flex-col items-center text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <Shield className="w-6 h-6 text-blue-500 mb-1" />
                  <span className="text-xs text-slate-600 dark:text-slate-400">Secure Payment</span>
                </div>
                <div className="flex flex-col items-center text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <Clock className="w-6 h-6 text-blue-500 mb-1" />
                  <span className="text-xs text-slate-600 dark:text-slate-400">Instant Access</span>
                </div>
                <div className="flex flex-col items-center text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <CreditCard className="w-6 h-6 text-blue-500 mb-1" />
                  <span className="text-xs text-slate-600 dark:text-slate-400">Cancel Anytime</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <Button
                  onClick={handlePaymentConfirmation}
                  disabled={processingPayment}
                  className="w-full bg-green-600 hover:bg-green-700 text-white h-12 text-base font-semibold"
                >
                  {processingPayment ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing Payment...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-5 h-5 mr-2" />
                      Confirm Payment & Activate Account
                    </>
                  )}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBackToForm}
                  disabled={processingPayment}
                  className="w-full"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Edit Details
                </Button>
              </div>

              {/* Footer Note */}
              <p className="text-center text-xs text-slate-400 mt-6">
                By confirming payment, you agree to our Terms of Service and Privacy Policy.
                Your subscription will automatically renew {billingCycle === 'monthly' ? 'monthly' : 'annually'}.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Valid token - show signup form
  return (
    <div className="h-screen overflow-y-auto bg-gradient-to-br from-blue-50 via-white to-green-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src="/logo.png" alt="DataCaffe Logo" className="w-16 h-16 object-contain" />
            <div className="flex flex-col">
              <span className="text-2xl font-bold tracking-tight">
                <span style={{ color: '#f97316' }}>ESG</span>
                <span className="text-blue-600 dark:text-blue-400">Caffe</span>
              </span>
              <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                Green Intelligence
              </span>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-slate-800 dark:text-white mb-2">
            Welcome to ESGCaffe
          </h1>
          <p className="text-slate-500 dark:text-slate-400">
            Complete your company registration to get started
          </p>
        </div>

        {/* Step indicator — payment already collected upstream, so this is a single-step onboarding */}
        <div className="flex items-center justify-center mb-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-4 py-1.5">
            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white">
              <Building2 className="w-3.5 h-3.5" />
            </div>
            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Company Info</span>
            <span className="text-xs text-slate-500 dark:text-slate-400">·</span>
            <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400 font-medium">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Payment received
            </span>
          </div>
        </div>

        {/* Invitation Details Banner */}
        {invitationData && (
          <div className="mb-6 space-y-3">
            <Alert className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800 dark:text-green-400">
                Your invitation has been verified. Please complete the form below to register your company.
              </AlertDescription>
            </Alert>

            {/* Plan summary card */}
            {(invitationData.planName || invitationData.price) && (
              <Card className="border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide font-medium">Invited Plan</p>
                        <p className="font-semibold text-slate-800 dark:text-white">{invitationData.planName || 'Standard'}</p>
                        {invitationData.planDescription && (
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{invitationData.planDescription}</p>
                        )}
                      </div>
                    </div>
                    {invitationData.price && (
                      <div className="text-right">
                        {parseFloat(invitationData.discount || '0') > 0 && (
                          <p className="text-xs text-slate-400 line-through">
                            ₹{formatINR(parseFloat(invitationData.price))}/{invitationData.billingCycle === 'monthly' ? 'mo' : 'yr'}
                          </p>
                        )}
                        <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                          ₹{formatINR(invitationData.finalPrice ?? parseFloat(invitationData.price))}
                        </p>
                        <p className="text-xs text-slate-500">/{invitationData.billingCycle === 'monthly' ? 'month' : 'year'}</p>
                      </div>
                    )}
                  </div>
                  {invitationData.invitedBy && (
                    <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                      <Mail className="w-3 h-3" />
                      <span>Invited by <span className="font-medium text-slate-700 dark:text-slate-300">{invitationData.invitedBy}</span></span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Signup Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Company Registration</CardTitle>
            <CardDescription>
              Set up your company profile and admin account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Company Information Section */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Building2 className="w-5 h-5 text-blue-500" />
                  <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                    Company Information
                  </h3>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="companyName">Company Name <span className="text-red-500">*</span></Label>
                    <Input
                      id="companyName"
                      {...register('companyName')}
                      placeholder="Acme Corporation"
                      className="mt-1"
                    />
                    {errors.companyName && (
                      <p className="text-sm text-red-500 mt-1">{errors.companyName.message}</p>
                    )}
                  </div>

                  {invitationData?.subdomain && (
                    <div>
                      <Label htmlFor="subdomain">Subdomain</Label>
                      <div className="mt-1 flex items-center gap-0">
                        <Input
                          id="subdomain"
                          value={invitationData.subdomain}
                          disabled
                          readOnly
                          className="bg-slate-100 dark:bg-slate-800 cursor-not-allowed rounded-r-none"
                        />
                        <span className="inline-flex items-center px-3 h-10 bg-slate-100 dark:bg-slate-800 border border-l-0 border-input rounded-r-md text-sm text-muted-foreground whitespace-nowrap">
                          {import.meta.env.VITE_TENANT_DOMAIN || '.esgcaffe.ai'}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        This subdomain has been assigned to your organization.
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="industry">Industry <span className="text-red-500">*</span></Label>
                      <Controller
                        name="industry"
                        control={control}
                        render={({ field }) => (
                          <SearchableSelect
                            options={INDUSTRIES}
                            value={field.value || ''}
                            onChange={field.onChange}
                            placeholder="Select industry"
                            searchPlaceholder="Search industries..."
                            emptyMessage="No industry found."
                          />
                        )}
                      />
                      {errors.industry && (
                        <p className="text-sm text-red-500 mt-1">{errors.industry.message}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="website">Website</Label>
                      <div className="relative mt-1">
                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="website"
                          {...register('website')}
                          placeholder="https://example.com"
                          className="pl-10"
                        />
                      </div>
                      {errors.website && (
                        <p className="text-sm text-red-500 mt-1">{errors.website.message}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="fyStartMonth">Financial Year Start Month <span className="text-red-500">*</span></Label>
                    <Controller
                      name="fyStartMonth"
                      control={control}
                      render={({ field }) => (
                        <Select
                          onValueChange={(value) => field.onChange(parseInt(value))}
                          value={field.value?.toString()}
                        >
                          <SelectTrigger className="mt-1">
                            <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                            <SelectValue placeholder="Select month" />
                          </SelectTrigger>
                          <SelectContent>
                            {MONTHS.map((month) => (
                              <SelectItem key={month.value} value={month.value.toString()}>
                                {month.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {errors.fyStartMonth && (
                      <p className="text-sm text-red-500 mt-1">{errors.fyStartMonth.message}</p>
                    )}
                    <p className="text-xs text-slate-500 mt-1">
                      Select the month when your company's financial year begins
                    </p>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Headquarters Address Section */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Building2 className="w-5 h-5 text-blue-500" />
                  <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                    Headquarters Address
                  </h3>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="unitCode">Unit / Building Code <span className="text-red-500">*</span></Label>
                    <Input
                      id="unitCode"
                      {...register('unitCode')}
                      placeholder="e.g., HQ, MFG-01, OFF-02"
                      className="mt-1"
                    />
                    {errors.unitCode && (
                      <p className="text-sm text-red-500 mt-1">{errors.unitCode.message}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="addressLine1">Address Line 1 <span className="text-red-500">*</span></Label>
                    <div className="mt-1">
                      <LocationAutocomplete
                        value={watch('addressLine1') || ''}
                        onChange={(val) => setValue('addressLine1', val, { shouldValidate: true })}
                        onSelect={(location: LocationResult) => {
                          setValue('addressLine1', location.addressLine1 || location.displayName.split(',').slice(0, 2).join(',').trim(), { shouldValidate: true });
                          if (location.city) setValue('city', location.city, { shouldValidate: true });
                          if (location.state) setValue('state', location.state, { shouldValidate: true });
                          if (location.country) setValue('country', location.country, { shouldValidate: true });
                          if (location.postalCode) setValue('postalCode', location.postalCode, { shouldValidate: true });
                        }}
                        placeholder="Type address to search..."
                      />
                    </div>
                    {errors.addressLine1 && (
                      <p className="text-sm text-red-500 mt-1">{errors.addressLine1.message}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="addressLine2">Address Line 2 (Optional)</Label>
                    <Input
                      id="addressLine2"
                      {...register('addressLine2')}
                      placeholder="Apartment, suite, etc."
                      className="mt-1"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city">City <span className="text-red-500">*</span></Label>
                      <Input
                        id="city"
                        {...register('city')}
                        placeholder="City"
                        className="mt-1"
                      />
                      {errors.city && (
                        <p className="text-sm text-red-500 mt-1">{errors.city.message}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="state">State / Province <span className="text-red-500">*</span></Label>
                      <Input
                        id="state"
                        {...register('state')}
                        placeholder="State or Province"
                        className="mt-1"
                      />
                      {errors.state && (
                        <p className="text-sm text-red-500 mt-1">{errors.state.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="country">Country <span className="text-red-500">*</span></Label>
                      <Input
                        id="country"
                        {...register('country')}
                        placeholder="Country"
                        className="mt-1"
                      />
                      {errors.country && (
                        <p className="text-sm text-red-500 mt-1">{errors.country.message}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="postalCode">Postal / ZIP Code <span className="text-red-500">*</span></Label>
                      <Input
                        id="postalCode"
                        {...register('postalCode')}
                        placeholder="Postal/ZIP code"
                        className="mt-1"
                      />
                      {errors.postalCode && (
                        <p className="text-sm text-red-500 mt-1">{errors.postalCode.message}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Admin User Section */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5 text-blue-500" />
                  <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                    Administrator Account
                  </h3>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="firstName">First Name <span className="text-red-500">*</span></Label>
                      <div className="relative mt-1">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="firstName"
                          {...register('firstName')}
                          placeholder="John"
                          className="pl-10"
                        />
                      </div>
                      {errors.firstName && (
                        <p className="text-sm text-red-500 mt-1">{errors.firstName.message}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="lastName">Last Name</Label>
                      <Input
                        id="lastName"
                        {...register('lastName')}
                        placeholder="Doe"
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email">Email Address <span className="text-red-500">*</span></Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="email"
                        type="email"
                        {...register('email')}
                        placeholder="john.doe@company.com"
                        className={`pl-10 ${invitationData?.email ? 'bg-slate-100 dark:bg-slate-800 cursor-not-allowed' : ''}`}
                        disabled={!!invitationData?.email}
                        readOnly={!!invitationData?.email}
                      />
                    </div>
                    {invitationData?.email && (
                      <p className="text-xs text-slate-500 mt-1">Email is pre-filled from your invitation and cannot be changed</p>
                    )}
                    {errors.email && (
                      <p className="text-sm text-red-500 mt-1">{errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="designation">Designation <span className="text-red-500">*</span></Label>
                    <div className="relative mt-1">
                      <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="designation"
                        {...register('designation')}
                        placeholder="ESG Manager"
                        className="pl-10"
                      />
                    </div>
                    {errors.designation && (
                      <p className="text-sm text-red-500 mt-1">{errors.designation.message}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="department">Department <span className="text-red-500">*</span></Label>
                    <Controller
                      name="department"
                      control={control}
                      render={({ field }) => (
                        <SearchableSelect
                          options={departmentOptions}
                          value={field.value || ''}
                          onChange={field.onChange}
                          placeholder="Select department"
                          searchPlaceholder="Search departments..."
                          emptyMessage="No department found."
                        />
                      )}
                    />
                    {errors.department && (
                      <p className="text-sm text-red-500 mt-1">{errors.department.message}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="password">Password <span className="text-red-500">*</span></Label>
                      <div className="relative mt-1">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          {...register('password')}
                          placeholder="••••••••"
                          className="pl-10 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                        >
                          {showPassword ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                      </div>
                      {errors.password && (
                        <p className="text-sm text-red-500 mt-1">{errors.password.message}</p>
                      )}
                      <PasswordStrengthChecklist password={watch('password') ?? ''} />
                    </div>

                    <div>
                      <Label htmlFor="confirmPassword">Confirm Password <span className="text-red-500">*</span></Label>
                      <div className="relative mt-1">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="confirmPassword"
                          type={showConfirmPassword ? 'text' : 'password'}
                          {...register('confirmPassword')}
                          placeholder="••••••••"
                          className="pl-10 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                        >
                          {showConfirmPassword ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                      </div>
                      {watch('confirmPassword') && watch('password') !== watch('confirmPassword') ? (
                        <p className="text-sm text-red-500 mt-1">Passwords do not match</p>
                      ) : errors.confirmPassword ? (
                        <p className="text-sm text-red-500 mt-1">{errors.confirmPassword.message}</p>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <Button
                  type="submit"
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white h-11"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Complete Setup
                </Button>
              </div>

              {/* Footer */}
              <div className="text-center text-sm text-slate-500 dark:text-slate-400">
                Already have an account?{' '}
                <Button
                  type="button"
                  variant="link"
                  className="p-0 h-auto text-blue-500 hover:text-blue-600"
                  onClick={() => navigate('/login')}
                >
                  Sign in
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AcceptInvitation;
